import { Link, PopoverOrigin, Stack, Typography } from '@mui/material'
import { format, isBefore } from 'date-fns'
import { find } from 'lodash'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeetingDTO } from '../../../../api/gen/base'
import ArrowRightIcon from '../../../../assets/icons/ArrowRight.svg?react'
import PenIcon from '../../../../assets/icons/Pen.svg?react'
import TrashIcon from '../../../../assets/icons/Trash.svg?react'
import { RECORD_ROUTE, RECORDS_ROUTE } from '../../../../constants/routing.constants'
import { MeetDeleteDialog } from '../../../../dialogs/MeetDeleteDialog/MeetDeleteDialog'
import { MeetEditDialog } from '../../../../dialogs/MeetEditDialog/MeetEditDialog'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { InfoPopover } from './InfoPopover'
import { PopoverFooterButton } from './PopoverFooterButton'

type Props = {
    anchorEl: HTMLElement | null
    onClose?: VoidFunction
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    data?: MeetingDTO
}

export const MeetsInfoPopover: FC<Props> = ({
    anchorEl,
    onClose,
    anchorOrigin,
    transformOrigin,
    data,
}) => {
    const navigate = useNavigate()

    const { projectListFull } = useWrapperStore((state) => state)

    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false)
    const [isOpenEditDialog, setIsOpenEditDialog] = useState(false)

    const projectName = useMemo(() => {
        const projectDetail = find(projectListFull, (item) => {
            return item.id === data?.workplace_id
        })

        return projectDetail?.title ?? 'Без проекта'
    }, [projectListFull])

    const handleOpenRecord = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()

        navigate(data?.record_id ? `${RECORD_ROUTE}/${data?.record_id}` : RECORDS_ROUTE)

        onClose?.()
    }

    const handleOpenDeleteDialog = () => {
        setIsOpenDeleteDialog(true)
        onClose?.()
    }
    const handleCloseDeleteDialog = () => setIsOpenDeleteDialog(false)

    const handleOpenEditDialog = () => {
        setIsOpenEditDialog(true)
        onClose?.()
    }
    const handleCloseEditDialog = () => setIsOpenEditDialog(false)

    const canEdit = !!data && isBefore(new Date(), data.start_at)

    return (
        <>
            <MeetDeleteDialog
                id={data?.id}
                open={isOpenDeleteDialog}
                handleClose={handleCloseDeleteDialog}
            />
            <MeetEditDialog
                idMeeting={data?.id}
                open={isOpenEditDialog}
                handleClose={handleCloseEditDialog}
                source={data?.source ?? ''}
            />
            <InfoPopover
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                title={data?.title ?? ''}
                footer={
                    canEdit ? (
                        <>
                            <PopoverFooterButton
                                startIcon={<TrashIcon color="#5A5F72" />}
                                onClick={handleOpenDeleteDialog}>
                                Удалить встречу
                            </PopoverFooterButton>
                            <PopoverFooterButton
                                startIcon={<PenIcon color="#5A5F72" />}
                                onClick={handleOpenEditDialog}>
                                Редактировать
                            </PopoverFooterButton>
                        </>
                    ) : (
                        <PopoverFooterButton
                            endIcon={<ArrowRightIcon color="#5A5F72" />}
                            onClick={handleOpenRecord}
                            sx={{
                                width: '100%',
                                '.MuiButton-endIcon': {
                                    marginLeft: 0,
                                },
                            }}>
                            К записи встречи
                        </PopoverFooterButton>
                    )
                }>
                <Stack direction="column" gap={1}>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Проект:
                        </Typography>
                        <Typography
                            variant="body2"
                            color="#181B25"
                            sx={{
                                width: '100%',
                                display: '-webkit-box',
                                WebkitLineClamp: '1',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {projectName}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Дата начала:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {data?.start_at ? format(data?.start_at, 'dd.MM.yyyy, HH:mm') : ''}
                        </Typography>
                    </Stack>
                    <Link
                        href={data?.source}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        color="#006AD8"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: '1',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {data?.source}
                    </Link>
                </Stack>
            </InfoPopover>
        </>
    )
}
